import { ComponentProps, useEffect, useMemo, useState } from 'react';
import type { UsersHostProps } from '../types';
import { useTranslation } from 'react-i18next';
import { Box, Chip, type AccountUserRole, DropdownMenu, Icon, MaterialSymbol, TextField, withStyles } from 'vopay';
import { Button, Card, Skeleton, Table, Title } from '#shared/components/UI';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useDialog } from '#shared/providers/Dialog';
import { useBlockingLoader } from '#shared/providers/Loader';
import { useToast } from '#shared/providers/Toast';
import CreateUserDrawer from '../CreateUserDrawer/CreateUserDrawer';
import ManageUserRolesDialog, { ManageUserRolesResult } from '../ManageUserRolesDialog/ManageUserRolesDialog';
import UserPermissionsDrawer from '../UserPermissionsDrawer/UserPermissionsDrawer';
import useUsers from '../useUsers';
import useUserRoleAssignment from '../useUserRoleAssignment';
import useDeleteUser from '../useDeleteUser';
import useResetPassword from '../useResetPassword';
import CreateRoleDrawer from '../UserRoles/CreateRoleDrawer';
import EditRoleDrawer from '../UserRoles/EditRoleDrawer';
import RoleDetailsDrawer from '../UserRoles/RoleDetailsDrawer';
import RolePermissionsViewDrawer from '../UserRoles/RolePermissionsViewDrawer';
import RoleUsersViewDrawer from '../UserRoles/RoleUsersViewDrawer';
import useDeleteRole from '../UserRoles/useDeleteRole';
import UserRolesTiles from '../UserRolesTiles/UserRolesTiles';
import useUsersRoles from '../useUsersRoles';
import styles from './UsersList.module.scss';

const cn = withStyles(styles);

type ActionsMenuItem = {
  id: string;
  label: string;
  icon: ComponentProps<typeof MaterialSymbol>['icon'];
  colorScheme?: 'default' | 'destructive';
  onSelect: () => void;
};

type UsersListProps = Pick<UsersHostProps, 'title' | 'titleLevel'>;

// The searchable user table with its per-row actions. Internal to the Users feature: reads the
// feature context, so it only mounts under the UsersPage root. Given a title (the host's inline
// placement), it renders it on the search row beside Add User; otherwise the root renders it above.
export default function UsersList({ title, titleLevel }: UsersListProps) {
  const { account, userPermissions, tableContainer = 'card', users, fetchingUsers, fetchUsers } = useUsers();

  const {
    roles,
    fetchingRoles,
    fetchRoles,
    fetchingRoleUsers,
    usersByRole,
    roleCounts,
    fetchRoleUsers,
    rolesByUsername,
  } = useUsersRoles();

  const { assignUserRole, unassignUserRole, fetchUserRoleAssignments } = useUserRoleAssignment();
  const { deleteUser } = useDeleteUser();
  const { resetPassword } = useResetPassword();

  const alertDialog = useAlertDialog();
  const dialog = useDialog();
  const blockingLoader = useBlockingLoader();
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.UsersList' });
  const { t: tErrors } = useTranslation('translations', { keyPrefix: 'Shared.Errors' });
  const { t: tManageRoles } = useTranslation('translations', { keyPrefix: 'Shared.ManageUserRolesDialog' });
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateUserDrawerOpen, setIsCreateUserDrawerOpen] = useState(false);
  const [permissionsEmail, setPermissionsEmail] = useState<string>();
  const [isPermissionsDrawerOpen, setIsPermissionsDrawerOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<AccountUserRole | null>(null);
  const [viewingPermissionsRole, setViewingPermissionsRole] = useState<AccountUserRole | null>(null);
  const [viewingUsersRole, setViewingUsersRole] = useState<AccountUserRole | null>(null);
  const { deleteRole } = useDeleteRole();
  const [editingRole, setEditingRole] = useState<AccountUserRole | null>(null);
  const [isCreateRoleDrawerOpen, setIsCreateRoleDrawerOpen] = useState(false);
  // Set when "+ New Role" was clicked inside Manage Role(s): the new role is then assigned to this user.
  const [pendingNewRoleAssignee, setPendingNewRoleAssignee] = useState<{ Username: string } | null>(null);

  // An in-flight Create User session paused while the admin creates a role. Non-null means the Create User
  // drawer reopens with this state once the Create Role drawer closes.
  const [pendingCreateUserState, setPendingCreateUserState] = useState<{
    emailAddress: string;
    selectedRoleIDs: number[];
  } | null>(null);

  useEffect(() => {
    fetchUsers().catch(() => {
      toast.neutral(t('Errors.loadUsers', { defaultValue: 'Could not load users' }));
    });
  }, [fetchUsers]);

  const canViewRoles = userPermissions.canViewRoles || userPermissions.canManageRoles;

  useEffect(() => {
    if (!canViewRoles) return;

    fetchRoles().catch(() => {
      toast.neutral(t('Errors.loadRoles', { defaultValue: 'Could not load roles' }));
    });
  }, [canViewRoles, fetchRoles]);

  // Counts and the Role(s) column both come from the per-role user lists, so they refresh whenever the
  // role list does.
  useEffect(() => {
    if (roles.length === 0) return;

    fetchRoleUsers(roles.map((role) => role.RoleID)).catch(() => {
      toast.neutral(t('Errors.loadRoleUserCounts', { defaultValue: 'Could not load role user counts' }));
    });
  }, [roles, fetchRoleUsers]);

  useEffect(() => {
    if (!isCreateRoleDrawerOpen && pendingCreateUserState && !isCreateUserDrawerOpen) {
      setIsCreateUserDrawerOpen(true);
    }
  }, [isCreateRoleDrawerOpen, pendingCreateUserState, isCreateUserDrawerOpen]);

  const filteredUsers = useMemo(
    () => users.filter((user) => searchTerm === '' || user.Username.toLowerCase().includes(searchTerm.toLowerCase())),
    [users, searchTerm],
  );

  async function handleRoleCreated(newRole: { RoleID: number; Name: string }) {
    const rolesRefresh = fetchRoles().catch(() => {
      toast.neutral(t('Errors.refreshRoles', { defaultValue: 'Could not refresh roles' }));
    });

    // Came from Create User's "+ New Role": pre-select the new role for when that drawer reopens.
    if (pendingCreateUserState) {
      setPendingCreateUserState({
        ...pendingCreateUserState,
        selectedRoleIDs: [...pendingCreateUserState.selectedRoleIDs, newRole.RoleID],
      });

      await rolesRefresh;

      return;
    }

    // Came from Manage Role(s): assign the new role to the user the admin was editing.
    if (!pendingNewRoleAssignee) return;

    const assignee = pendingNewRoleAssignee;

    setPendingNewRoleAssignee(null);

    try {
      await assignUserRole({ Username: assignee.Username, RoleID: newRole.RoleID });

      toast.success(
        t('Actions.roleCreatedAndAssigned', {
          defaultValue: 'Role "{{roleName}}" was created and assigned to {{username}}.',
          roleName: newRole.Name,
          username: assignee.Username,
        }),
      );

      // The refreshed roles also refresh the counts and the Role(s) column.
      await rolesRefresh;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : tErrors('unknownError', { defaultValue: 'Unknown error' });

      // An error, not a neutral toast: the role exists but the chained assignment didn't land, and recovery
      // is manual via Manage Role(s).
      alertDialog.error(
        t('Actions.roleAssignFailedTitle', { defaultValue: 'Role created but not assigned' }),
        t('Actions.roleAssignFailed', {
          defaultValue:
            'Role "{{roleName}}" was created, but could not be assigned to {{username}}: {{errorMessage}}\n\nYou can assign it manually from Actions → Manage Role(s).',
          roleName: newRole.Name,
          username: assignee.Username,
          errorMessage,
        }),
      );
    }
  }

  // a request to create a new role (remembered so the new role is assigned once it exists).
  async function handleManageRoles(user: { Username: string }) {
    const result = await dialog.open<ManageUserRolesResult>(
      (dialogProps) => (
        <ManageUserRolesDialog
          {...dialogProps}
          user={user}
          availableRoles={roles}
          canCreateRole={userPermissions.canManageRoles}
          fetchUserRoleAssignments={fetchUserRoleAssignments}
        />
      ),
      { bare: true },
    );

    if (!result) return;

    if (result.action === 'createRole') {
      setPendingNewRoleAssignee(user);
      setIsCreateRoleDrawerOpen(true);

      return;
    }

    const { added, removed } = result;

    try {
      await Promise.all([
        ...added.map((RoleID) => assignUserRole({ Username: user.Username, RoleID })),
        ...removed.map((RoleID) => unassignUserRole({ Username: user.Username, RoleID })),
      ]);

      if (removed.length === 0) {
        toast.success(tManageRoles('Toast.roleAssigned', { defaultValue: 'Role Assigned' }));
      } else if (added.length === 0) {
        toast.success(tManageRoles('Toast.roleUnassigned', { defaultValue: 'Role Unassigned' }));
      } else {
        toast.success(tManageRoles('Toast.rolesUpdated', { defaultValue: 'Roles updated' }));
      }

      // Tile counts and the Role(s) column both read the per-role user lists.
      fetchRoleUsers(roles.map((role) => role.RoleID)).catch(() => {
        toast.neutral(t('Errors.refreshRoleUserCounts', { defaultValue: 'Could not refresh role user counts' }));
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : tErrors('unknownError', { defaultValue: 'Unknown error' });

      toast.neutral(
        tManageRoles('Toast.error', {
          defaultValue: 'Could not update role assignments: {{errorMessage}}',
          errorMessage,
        }),
      );
    }
  }

  async function handleDeleteRole() {
    if (!selectedRole) return;

    const isDeleted = await deleteRole(selectedRole, roleCounts[selectedRole.RoleID] ?? 0);

    if (!isDeleted) return;

    setSelectedRole(null);
    setViewingPermissionsRole(null);
    setViewingUsersRole(null);

    fetchRoles().catch(() => {
      toast.neutral(t('Errors.refreshRoles', { defaultValue: 'Could not refresh roles' }));
    });
  }

  async function handleResetPassword(username: string) {
    const result = await alertDialog.open(
      t('ResetPassword.title', { defaultValue: 'Send Password Reset Email?' }),
      t('ResetPassword.description', {
        defaultValue: 'This will send a secure password reset link to the user’s email address.',
      }),
      {
        abortLabel: t('ResetPassword.cancelButton', { defaultValue: 'Cancel' }),
        confirmLabel: t('ResetPassword.sendButton', { defaultValue: 'Send Reset Email' }),
      },
    );

    if (result !== 'confirm') return;

    try {
      await resetPassword({ Username: username });
      toast.success(t('ResetPassword.success', { defaultValue: 'The link was sent successfully.' }));
    } catch {
      toast.neutral(t('ResetPassword.fail', { defaultValue: 'Unable to send the link.' }));
    }
  }

  // alertDialog (Radix) rather than a confirm dialog: this list can render inside a host's modal
  // SideDrawer, where a non-Radix modal would be inert.
  async function handleDeleteUser(username: string) {
    const result = await alertDialog.caution(
      t('DeleteUser.confirmTitle', { defaultValue: 'Are you sure you want to delete the user?' }),
      t('DeleteUser.confirmSubTitle', { defaultValue: 'This action cannot be undone.' }),
      {
        abortLabel: t('DeleteUser.cancelLabel', { defaultValue: 'No' }),
        confirmLabel: t('DeleteUser.confirmLabel', { defaultValue: 'Yes' }),
      },
    );

    if (result !== 'confirm') return;

    blockingLoader.show();

    try {
      await deleteUser({ EmailAddress: username });
      toast.success(t('DeleteUser.success', { defaultValue: 'Success! The user was successfully deleted.' }));

      fetchUsers().catch(() => {
        toast.neutral(t('Errors.refreshUsers', { defaultValue: 'Could not refresh the user list' }));
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : tErrors('unknownError', { defaultValue: 'Unknown error' });

      alertDialog.error(t('DeleteUser.errorTitle', { defaultValue: 'Error! The user was not deleted.' }), errorMessage);
    } finally {
      blockingLoader.hide();
    }
  }

  const tableContent = fetchingUsers ? (
    <Skeleton height="320px" />
  ) : (
    <Table
      id="users-list"
      zebra
      columns={[
        {
          id: 'Username',
          header: t('Table.email', { defaultValue: 'Email Address' }),
          align: 'left',
          field: 'Username',
        },
        {
          id: 'DateAdded',
          header: t('Table.dateAdded', { defaultValue: 'Date Added' }),
          align: 'left',
          field: 'DateAdded',
        },
        {
          id: 'Roles',
          header: t('Table.roles', { defaultValue: 'Role(s)' }),
          align: 'left',
          template: ({ data }) => {
            const userRoles = rolesByUsername[data.Username.toLowerCase()] ?? [];

            if (userRoles.length === 0) return <span>-</span>;

            return (
              <Box flx="row" gapSize="6px" wrap="wrap">
                {userRoles.map((role) => (
                  <Chip key={role.RoleID} label={role.Name} />
                ))}
              </Box>
            );
          },
          visible: roles.length > 0,
        },
        {
          id: 'view',
          header: '',
          align: 'right',
          template: ({ data }) =>
            data.ActionsMenu.length === 0 ? null : (
              <Box jc="fe" gap="sm">
                <DropdownMenu>
                  <DropdownMenu.Trigger asChild>
                    <Button size="small" colorScheme="secondary" iconRight="keyboard_arrow_down">
                      {t('Actions.actions', { defaultValue: 'Actions' })}
                    </Button>
                  </DropdownMenu.Trigger>

                  <DropdownMenu.Content align="end">
                    {data.ActionsMenu.map((item) => (
                      <DropdownMenu.Item
                        key={item.id}
                        onSelect={() => item.onSelect()}
                        colorScheme={item.colorScheme}
                        left={
                          <MaterialSymbol
                            icon={item.icon}
                            size={18}
                            color={item.colorScheme === 'destructive' ? '#e8160b' : undefined}
                          />
                        }
                      >
                        {item.label}
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.Content>
                </DropdownMenu>
              </Box>
            ),
        },
      ]}
      rows={filteredUsers.map((user) => {
        const items: ActionsMenuItem[] = [];

        if (userPermissions.canManageUserPermissions) {
          items.push({
            id: `user-select-role-${user.Username}`,
            label: t('Actions.selectRole', { defaultValue: 'Manage Role(s)' }),
            icon: 'groups_2',
            onSelect: () => {
              handleManageRoles({ Username: user.Username });
            },
          });

          items.push({
            id: `user-change-permissions-${user.Username}`,
            label: t('Actions.permissions', { defaultValue: 'Permissions' }),
            icon: 'settings',
            onSelect: () => {
              setPermissionsEmail(user.Username);
              setIsPermissionsDrawerOpen(true);
            },
          });
        }

        if (userPermissions.canAddUsers) {
          items.push({
            id: `user-reset-password-${user.Username}`,
            label: t('Actions.resetPassword', { defaultValue: 'Reset Password' }),
            icon: 'refresh',
            onSelect: () => {
              handleResetPassword(user.Username);
            },
          });

          items.push({
            id: `user-delete-${user.Username}`,
            label: t('Actions.remove', { defaultValue: 'Remove' }),
            icon: 'close',
            colorScheme: 'destructive',
            onSelect: () => {
              handleDeleteUser(user.Username);
            },
          });
        }

        return {
          id: user.Username,
          data: {
            Username: user.Username,
            DateAdded: user.DateAdded,
            ActionsMenu: items,
          },
        };
      })}
    />
  );

  return (
    <div>
      <div className={cn('header')}>
        {title && <Title level={titleLevel}>{title}</Title>}

        <div className={cn('controls')}>
          <TextField
            name="UserSearchKeyword"
            placeholder={t('Search.placeholder', { defaultValue: 'Search user' })}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlineShadow"
            elementLeft={
              <div className={cn('searchIcon')}>
                <Icon icon="search" width="17px" height="17px" color="none" />
              </div>
            }
          />

          {userPermissions.canAddUsers && (
            <Button id="addUser" onClick={() => setIsCreateUserDrawerOpen(true)} colorScheme="primary" size="small">
              {t('Actions.addUser', { defaultValue: 'Add User' })}
            </Button>
          )}
        </div>
      </div>

      <UserRolesTiles
        roles={roles}
        roleCounts={roleCounts}
        fetching={fetchingRoles}
        onRoleClick={setSelectedRole}
        onCreateRoleClick={() => setIsCreateRoleDrawerOpen(true)}
      />

      {tableContainer === 'bare' ? (
        tableContent
      ) : (
        <Card className={cn('tableCard')} data-testid="usersListTableCard">
          {tableContent}
        </Card>
      )}

      <RoleDetailsDrawer
        isOpen={!!selectedRole && !editingRole}
        role={selectedRole}
        usersCount={selectedRole ? (roleCounts[selectedRole.RoleID] ?? 0) : 0}
        isFetchingUsers={fetchingRoleUsers}
        hasSubAccounts={account.hasSubAccounts}
        canManage={userPermissions.canManageRoles}
        onClose={() => setSelectedRole(null)}
        onEdit={() => setEditingRole(selectedRole)}
        onDelete={handleDeleteRole}
        onViewPermissions={() => setViewingPermissionsRole(selectedRole)}
        onViewUsers={() => setViewingUsersRole(selectedRole)}
      />

      <RolePermissionsViewDrawer
        isOpen={!!viewingPermissionsRole}
        role={viewingPermissionsRole}
        onClose={() => setViewingPermissionsRole(null)}
      />

      <RoleUsersViewDrawer
        isOpen={!!viewingUsersRole}
        role={viewingUsersRole}
        users={viewingUsersRole ? (usersByRole[viewingUsersRole.RoleID] ?? []) : []}
        isFetching={fetchingRoleUsers}
        onClose={() => setViewingUsersRole(null)}
      />

      <CreateRoleDrawer
        isOpen={isCreateRoleDrawerOpen}
        suppressSuccessToast={!!pendingNewRoleAssignee}
        onClose={() => {
          setIsCreateRoleDrawerOpen(false);
          // Covers Cancel: the assignee is only meant to survive a successful create.
          setPendingNewRoleAssignee(null);
        }}
        onRoleCreated={handleRoleCreated}
      />

      <EditRoleDrawer
        isOpen={!!editingRole}
        role={editingRole}
        onClose={() => {
          setEditingRole(null);
          setSelectedRole(null);
        }}
        onRoleUpdated={() => {
          setSelectedRole(null);

          fetchRoles().catch(() => {
            toast.neutral(t('Errors.refreshRoles', { defaultValue: 'Could not refresh roles' }));
          });
        }}
      />

      <UserPermissionsDrawer
        isOpen={isPermissionsDrawerOpen}
        onClose={() => setIsPermissionsDrawerOpen(false)}
        emailAddress={permissionsEmail}
        availableRoles={roles}
      />

      <CreateUserDrawer
        isOpen={isCreateUserDrawerOpen}
        existingUsernames={users.map((user) => user.Username)}
        availableRoles={roles}
        initialEmailAddress={pendingCreateUserState?.emailAddress}
        initialRoleIDs={pendingCreateUserState?.selectedRoleIDs}
        onClose={() => {
          setIsCreateUserDrawerOpen(false);
          // The drawer is closing for real (submit or cancel): drop any paused session.
          setPendingCreateUserState(null);
        }}
        onCreateNewRole={
          userPermissions.canManageRoles
            ? (currentState) => {
                setPendingCreateUserState(currentState);
                // Closed directly rather than through onClose, which would clear the paused session.
                setIsCreateUserDrawerOpen(false);
                setIsCreateRoleDrawerOpen(true);
              }
            : undefined
        }
        onUserCreated={() => {
          fetchUsers().catch(() => {
            toast.neutral(t('Errors.refreshUsers', { defaultValue: 'Could not refresh the user list' }));
          });

          // Roles picked during creation change the Role(s) column; the tile counts follow the role refresh.
          if (canViewRoles) {
            fetchRoles().catch(() => {
              toast.neutral(t('Errors.refreshRoles', { defaultValue: 'Could not refresh roles' }));
            });
          }
        }}
      />
    </div>
  );
}
